import { MonoTypeOperatorFunction, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { WritableSignal } from '@angular/core';

/**
 * RxJS operator that manages a boolean signal with a delay to prevent UI flickering.
 *
 * Toggles the signal to true only if the observable takes longer than the specified delay.
 * This prevents the "flickering" effect when operations complete quickly.
 *
 * @param signal - Angular writable signal to control state (e.g., loading, saving, processing)
 * @param delayMs - Delay in milliseconds before toggling signal to true (default: 1000ms)
 *
 * @example
 * ```typescript
 * public readonly loading = signal<boolean>(false);
 *
 * this._http.get('/api/data').pipe(
 *   delayedToggle(this.loading, 1000)
 * ).subscribe(result => {
 *   // Handle result
 * });
 * ```
 *
 * @example With custom delay
 * ```typescript
 * // Show loading after 500ms
 * this._service.getData().pipe(
 *   delayedToggle(this.loading, 500)
 * ).subscribe();
 * ```
 */
export function delayedToggle<T>(
  signal: WritableSignal<boolean>,
  delayMs = 1000,
): MonoTypeOperatorFunction<T> {
  return (source: Observable<T>): Observable<T> => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    return new Observable<T>((subscriber) => {
      // Start timer: toggle signal only if request takes longer than delay
      timeoutId = setTimeout(() => {
        signal.set(true);
        timeoutId = null;
      }, delayMs);

      // Subscribe to source observable
      const subscription = source
        .pipe(
          finalize(() => {
            // Clear timeout if response arrives before delay
            if (timeoutId) {
              clearTimeout(timeoutId);
              timeoutId = null;
            }
            // Reset signal if it was toggled
            signal.set(false);
          }),
        )
        .subscribe({
          next: (value) => subscriber.next(value),
          error: (error) => subscriber.error(error),
          complete: () => subscriber.complete(),
        });

      // Cleanup function
      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        subscription.unsubscribe();
      };
    });
  };
}
